import React, { useEffect } from 'react';
import Toolbar from 'src/components/Toolbar';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/Button';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import './style.scoped.css';
import iconShieldCheck from 'src/assets/shield_check_outline.svg';
import iconDollar from 'src/assets/dollar.svg';
import iconLight from 'src/assets/light.svg';
import iconThumb from 'src/assets/thumb_up.svg';
import { confirmQuitFlow, goBack } from 'src/utils';
import homeStore, { HomeStore } from 'src/stores/HomeStore';
import { observer } from 'mobx-react-lite';
import { gaTrackEvent } from '../../api';
import { BUYING_PROCESS, BUYING_PROCESS_STEP, BUYING_PROCESS_TRACKING } from '../../constants';
import UserInfoService from '../../utils/UserInfoService';
dayjs.extend(customParseFormat);

interface CancerInsurranceInfoObserverProps {
  store?: HomeStore;
}

const CancerInsurranceInfoObserver = observer(
  ({ store }: CancerInsurranceInfoObserverProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    // const dispatch = useDispatch();

    useEffect(() => {
      homeStore.fetchOnlineProductHome();
    }, []);

    const _handleBuyInsurrance = () => {
      console.log('_handleBuyInsurrance');
      history.push('/buy-insurance/cancer/customer-info');
      gaTrackEvent(BUYING_PROCESS_TRACKING,
        BUYING_PROCESS[BUYING_PROCESS_STEP.ClickBuyInsurance].name,
        UserInfoService.getUserInfo()?.idCardNo ?? "");
    };

    const _renderFeeInfo = () => {
      return (
        <div className={'infoCard'}>
          <div className={'cardHeader titleText text-onSurface'}>
            {t('important_feature')}
          </div>
          <div className="cardRowNoBorder pt-4">
            <div className="iconContainer">
              <img src={iconDollar} />
            </div>
            <div className="flex flex-col">
              <div className="labelText">
                Tổng quyền lợi bảo hiểm lên đến{' '}
                <span className="smallTitlePrimary">625 triệu đồng</span> với
                mức phí chỉ từ khoảng{' '}
                <span className="smallTitlePrimary">500 đồng/ngày</span>
              </div>
              <div className="h-4" />
              <div className="flex flex-row">
                <div className="dotContainer">
                  <div className="dot" />
                </div>
                <div className="labelText">
                  Nhận ngay 100% STBH khi có chẩn đoán bệnh ung thư ở bất kỳ
                  giai đoạn nào (*)
                </div>
              </div>
              <div className="h-4" />
              <div className="flex flex-row">
                <div className="dotContainer">
                  <div className="dot" />
                </div>
                <div className="labelText">
                  Nhận thêm 25% STBH khi bạn sống khỏe sau 2 năm kể từ ngày chẩn
                  đoán mắc bệnh ung thư{' '}
                  <span className="smallTitlePrimary">LẦN ĐẦU TIÊN</span> tại MB
                  Ageas
                </div>
              </div>
            </div>
          </div>
          <div className="cardRowNoBorder">
            <div className="iconContainer">
              <img src={iconThumb} />
            </div>
            <div className="flex flex-col">
              <div className="labelText text-onSurface flex">
                {t('cancer_feature_2')}
              </div>
            </div>
          </div>
          <div className="cardRowNoBorder">
            <div className="iconContainer">
              <img src={iconLight} />
            </div>
            <div className="flex flex-col">
              <div>
                <div className="labelText">{t('cancer_feature_3')}</div>
              </div>
            </div>
          </div>
        </div>
      );
    };

    const _renderMoreInfo = () => {
      return (
        <div className={'infoCard'}>
          <div className={'cardHeader flex flex-row items-center'}>
            <div className={'titleText text-onSurface'}>
              {t('more_info_about_product')}
            </div>
          </div>
          <div className="px-3.5 pt-5 pb-6">
            <div className={'grid grid-flow-row grid-cols-2 grid-rows-2 gap-2'}>
              <div className="bg-background rounded-lg p-4">
                <div className="mb-1 labelText">{t('age_join_insurrance')}</div>
                <div className="titleText text-primary">18 tuổi – 65 tuổi</div>
              </div>
              <div className="bg-background rounded-lg p-4">
                <div className="mb-1 labelText">{t('term_fee')}</div>
                <div className="titleText text-primary">Hàng năm</div>
              </div>
              <div className="bg-background rounded-lg p-4">
                <div className="mb-1 labelText">{t('contract_term')}</div>
                <div className="titleText text-primary">5 năm</div>
              </div>
              <div className="bg-background rounded-lg p-4">
                <div className="mb-1 labelText">
                  {t('fee_payment_deadline')}
                </div>
                <div className="titleText text-primary">5 năm</div>
              </div>
            </div>
            <div className="h-4" />
            <div className="noteText text-onSurfaceVariant">
              {t('cancer_info_note')}
            </div>
          </div>
        </div>
      );
    };

    const _handleClose = () => {
      confirmQuitFlow(history);
    };

    const _handleBack = () => {
      goBack();
    };

    return (
      <div className="screen-container bg-background">
        <Toolbar title={t('buy_insurrance')} onBack={_handleBack} />
        <div className="max-w-md mx-auto screenScrollContainer">
          <div className="relative">
            <img
              src={store?.cancerOnlineProduct?.photoDetail}
              className={'insurranceImage'}
            />
            <div className="insurranceImageGradient" />
          </div>
          <div className="pt-3 pb-20 bg-attention flex flex-col items-center">
            <div className="largeTitleText text-onPrimary mb1.5">
              {t('cancer_insurrance_name')}
            </div>
            <div className="subTitleText text-attentionLight">
              {t('cancer_insurrance_desc')}
            </div>
          </div>
          <div className="overlapContent">
            {_renderFeeInfo()}
            {_renderMoreInfo()}
          </div>
        </div>
        <div className="bottomButtonBlock">
          <Button
            text={t('buy_insurrance')}
            onClick={_handleBuyInsurrance}
            extraClassName={'w-full'}
          />
        </div>
      </div>
    );
  }
);

const CancerInsurranceInfo = () => (
  <CancerInsurranceInfoObserver store={homeStore} />
);
export default CancerInsurranceInfo;
